import React from "react";
import type { OrderTodo, TodoStatus } from "../../../../domain/types/todo";
import { TODO_STATUS_LABELS } from "../../../../domain/types/todo";
import { TodoKanbanCard } from "./TodoKanbanCard";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface TodoKanbanColumnProps {
  status: TodoStatus;
  todos: OrderTodo[];
  onTodoClick: (todo: OrderTodo) => void;
}

interface SortableTodoCardProps {
  todo: OrderTodo;
  onClick: (todo: OrderTodo) => void;
}

const SortableTodoCard: React.FC<SortableTodoCardProps> = ({ todo, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: todo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TodoKanbanCard todo={todo} onClick={onClick} isDragging={isDragging} />
    </div>
  );
};

// 컬럼별 배경색 (관리자 홈 대시보드와 동일)
// 작업전/대기중: 회색, 작업중: 옅은 하늘색, 작업완료: 짙은 하늘색
const getColumnBgColor = (status: TodoStatus): string => {
  switch (status) {
    case "PENDING":
    case "WAITING":
      return "#F2F2F2"; // 회색
    case "IN_PROGRESS":
      return "#F5F8FF"; // 옅은 하늘색
    case "COMPLETED":
      return "#E8EFFF"; // 짙은 하늘색
    default:
      return "#F2F2F2";
  }
};

export const TodoKanbanColumn: React.FC<TodoKanbanColumnProps> = ({
  status,
  todos,
  onTodoClick,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  const bgColor = getColumnBgColor(status);

  return (
    <div className="flex flex-col p-[1.5px] min-w-[240px] w-[240px] flex-shrink-0">
      {/* Column Header */}
      <div className="text-center mb-[0.25rem]">
        <h3 className="text-sm text-start ml-2 font-medium text-gray-700 mb-[0.25rem]">
          {TODO_STATUS_LABELS[status]} ({todos.length})
        </h3>
      </div>

      {/* Column Body */}
      <div
        ref={setNodeRef}
        className={`overflow-hidden min-h-[37.5rem] h-full rounded-[0.5rem] p-[0.5rem] transition-colors ${
          isOver ? "bg-blue-50 ring-2 ring-blue-300" : ""
        }`}
        style={{ backgroundColor: isOver ? undefined : bgColor }}
      >
        <div className="relative">
          <SortableContext
            items={todos.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3 min-h-[100px]">
              {todos.map((todo) => (
                <SortableTodoCard
                  key={todo.id}
                  todo={todo}
                  onClick={onTodoClick}
                />
              ))}
            </div>
          </SortableContext>

        </div>
      </div>
    </div>
  );
};

export default TodoKanbanColumn;
